<script lang="ts">
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import SbRichtext from '../components/sbRichtext.svelte';
	import type { SbImage, SbLink } from '../types';
	import BtnPrimary from '$lib/buttons/v2/btnPrimary.svelte';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import type { BtnTheme } from '$lib/buttons/v2/types';
	import Image from '../components/image.svelte';

	interface Props {
		content: ISbStoryData<{
			background_style: 'green-1' | 'green-6' | 'stone-3' | 'stone-4' | 'outline' | 'image';
			headline: string;
			text: ISbRichtext;
			cta_label: string;
			cta_title?: string;
			cta_url: SbLink;
			image?: SbImage;
		}>['content'];
	}

	let { content }: Props = $props();

	function getStyles(bgStyle: typeof content.background_style): {
		card: string;
		button: BtnTheme;
	} {
		switch (bgStyle) {
			case 'stone-3':
				return { card: 'bg-stone-3 text-brown-2', button: 'default' };
			case 'stone-4':
				return { card: 'bg-stone-4 text-brown-2', button: 'default' };
			case 'outline':
				return { card: 'bg-stone-4 text-brown-2 border border-stone-1', button: 'default' };
			case 'image':
				return { card: 'text-stone-5', button: 'default' };
			case 'green-6':
				return { card: 'bg-green-6 text-brown-2', button: 'default' };
			case 'green-1':
			default:
				return { card: 'bg-green-1 text-stone-5', button: 'lime-light' };
		}
	}

	let { card, button } = getStyles(content.background_style);

	let hasBgImage = $derived(content.background_style === 'image');
</script>

<section
	use:editable={content}
	class="relative flex rounded-2xl max-lg:flex-col lg:min-h-[244px] {card}"
>
	<div
		class="flex flex-col pl-6 pt-6 max-lg:pb-8 max-lg:pr-6 lg:py-10 lg:pl-10 lg:pr-8 {!hasBgImage &&
		content.image
			? 'max-lg:flex-1 lg:w-2/3'
			: 'flex-1'}"
	>
		{#if hasBgImage && content.image}
			<Image
				src={content.image.filename}
				class="absolute inset-0 h-full w-full rounded-2xl object-cover"
			/>
			<div class="image-overlay absolute inset-0 rounded-2xl"></div>
		{/if}

		<div class="relative mb-8 lg:mb-6">
			<h1
				class="mb-2 font-semibold lg:mb-1 {hasBgImage
					? 'text-[32px]/[110%]'
					: 'text-[24px]/[110%] lg:text-[32px]/[110%]'}"
			>
				{content.headline}
			</h1>
			<div class={hasBgImage ? 'text-[18px]/[140%]' : 'text-[16px]/[140%] lg:text-[18px]/[140%]'}>
				<SbRichtext text={content.text} />
			</div>
		</div>

		<BtnPrimary
			theme={button}
			class="self-start"
			href={getSbUrl(content.cta_url)}
			title={content.cta_title}
			target={content.cta_url.target}>{content.cta_label}</BtnPrimary
		>
	</div>
	{#if !hasBgImage && content.image}
		<Image
			src={content.image.filename}
			alt={content.image.alt}
			title={content.image.title}
			wrapperClass="flex-1 lg:h-full w-full lg:w-1/3"
			class="h-full w-full object-cover object-top max-lg:aspect-4/3 max-lg:rounded-b-2xl lg:rounded-r-2xl lg:object-center"
		/>
	{/if}
</section>

<style>
	div.image-overlay {
		background: linear-gradient(0deg, rgba(36, 13, 2, 0) 0%, rgba(36, 13, 2, 0.68) 100%);

		@media (min-width: 1024px) {
			background: linear-gradient(270deg, rgba(36, 13, 2, 0) 0%, rgba(36, 13, 2, 0.68) 100%);
		}
	}
</style>
